// /src/lib/tracking.js
// Tracking architecture:
//   - Client-side: Meta Pixel (fbq) + GA4/Google Ads (gtag) sparati DIRETTAMENTE
//   - Server-side: POST /api/tracking/event → backend FastAPI → Meta CAPI + GA4 MP
//     con dedup tramite event_id condiviso col client.
//   - Make.com webhook: chiamato SOLO sul Lead per notifiche interne (CRM/Sheets/WhatsApp).

const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TRACKING_ENDPOINT = `${BACKEND_URL}/api/tracking/event`;

// Google Ads conversion identifier per "Lead generato"
const GA4_MEASUREMENT_ID = 'G-X511N9LBNZ';
const GOOGLE_ADS_ACCOUNT_ID = 'AW-17483775489';
const GOOGLE_ADS_LEAD_CONVERSION_ID = `${GOOGLE_ADS_ACCOUNT_ID}/VU1jCOe46d8bEIH89JBB`;

export const getCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return '';
};

export const generateEventId = () =>
  'evt_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);

/** Estrae il GA4 Client ID dal cookie `_ga` (formato GA1.2.<browser_id1>.<browser_id2>). */
const getGaClientId = () => {
  const ga = getCookie('_ga');
  if (!ga) return '';
  const parts = ga.split('.');
  if (parts.length >= 4) return parts.slice(-2).join('.');
  return ga;
};

/** Estrae il GA4 Session ID dal cookie `_ga_<MEASUREMENT_ID_SUFFIX>` (GS1.1.<session_id>.<count>.<engaged>.<timestamp>.<...>). */
const getGaSessionId = () => {
  if (typeof document === 'undefined') return '';
  // I cookie sessione GA4 hanno formato _ga_XXXXXXXXXX (es. _ga_X511N9LBNZ)
  const match = document.cookie.match(/_ga_[A-Z0-9]+=GS\d+\.\d+\.(\d+)\./);
  return match ? match[1] : '';
};

const getGclidFromCookies = () => {
  const explicit = getCookie('gclid');
  if (explicit) return explicit;
  const gclAw = getCookie('_gcl_aw');
  if (gclAw) {
    const parts = gclAw.split('.');
    return parts.length >= 3 ? parts.slice(2).join('.') : '';
  }
  return '';
};

/* --- IP cache ----------------------------------------------------- */
let cachedIp = '';
let ipPromise = null;
const fetchPublicIp = () => {
  if (cachedIp) return Promise.resolve(cachedIp);
  if (ipPromise) return ipPromise;
  ipPromise = fetch('https://api.ipify.org?format=json')
    .then((r) => r.json())
    .then((j) => { cachedIp = j && j.ip ? j.ip : ''; return cachedIp; })
    .catch((err) => { ipPromise = null; console.debug('[Sfarinà Tracking] ip fetch error', err); return ''; });
  return ipPromise;
};
if (typeof window !== 'undefined') fetchPublicIp();

/* --- Lead persistence con consent gate Iubenda ------------------- */
const LEAD_STORAGE_KEY = 'sfarina_lead_data';

const hasMarketingConsent = () => {
  if (typeof window === 'undefined') return false;
  try {
    const api = window._iub && window._iub.cs && window._iub.cs.api;
    if (!api || typeof api.getPreferences !== 'function') return false;
    const prefs = api.getPreferences() || {};
    return prefs.purposes && prefs.purposes['4'] === true;
  } catch (err) {
    console.debug('[Sfarinà Tracking] iubenda consent check failed', err);
    return false;
  }
};

export const persistLeadData = (leadData) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      LEAD_STORAGE_KEY,
      JSON.stringify({
        first_name: leadData.first_name || '',
        last_name: leadData.last_name || '',
        email: leadData.email || '',
        phone: leadData.phone || '',
      })
    );
  } catch (err) { console.debug('[Sfarinà Tracking] persistLeadData failed', err); }
};

const getPersistedLeadDataIfAllowed = () => {
  if (typeof window === 'undefined') return {};
  if (!hasMarketingConsent()) return {};
  try {
    const raw = sessionStorage.getItem(LEAD_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }
};

/* ================================================================ */
/* CLIENT-SIDE PIXEL FIRING (Meta Pixel + GA4 + Google Ads)         */
/* ================================================================ */
const FB_STANDARD_EVENTS = new Set([
  'PageView', 'Lead', 'ViewContent', 'Contact', 'AddToCart',
  'AddPaymentInfo', 'CompleteRegistration', 'InitiateCheckout',
  'Purchase', 'Search', 'StartTrial', 'Subscribe', 'AddToWishlist',
  'CustomizeProduct', 'Donate', 'FindLocation', 'Schedule', 'SubmitApplication',
]);

const toGaEventName = (eventName) => {
  const map = {
    PageView: 'page_view',
    Lead: 'generate_lead',
    ButtonClick: 'button_click',
    Scroll90: 'scroll',
    ViewSection: 'view_section',
    FormView: 'form_view',
    FormStart: 'form_start',
    CallIntent: 'call_intent',
  };
  if (map[eventName]) return map[eventName];
  return eventName
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
};

const fireClientSidePixels = (eventName, event_id, customData = {}) => {
  if (typeof window === 'undefined') return;

  // --- Meta Pixel -------------------------------------------------
  if (typeof window.fbq === 'function') {
    try {
      const fbMethod = FB_STANDARD_EVENTS.has(eventName) ? 'track' : 'trackCustom';
      const safeParams = {
        content_name: customData.button_label || customData.section || customData.context || '',
        content_category: customData.source || customData.sede || '',
      };
      // Per Lead, aggiungi value + currency (best practice Meta)
      if (eventName === 'Lead') {
        safeParams.value = customData.value != null ? customData.value : 0;
        safeParams.currency = customData.currency || 'EUR';
      }
      window.fbq(fbMethod, eventName, safeParams, { eventID: event_id });
    } catch (err) { console.debug('[Sfarinà Tracking] fbq fire failed', err); }
  }

  // --- GA4 (gtag) — SOLO GA4: NO Google Ads (per evitare doppio firing su AW-).
  //   Google Ads riceve la conversion separatamente via fireGoogleAdsLeadConversion()
  //   con send_to specifico = AW-.../<label>. Il parametro `send_to: G-...` forza
  //   il routing a un singolo container, evitando il fan-out automatico di gtag.
  if (typeof window.gtag === 'function') {
    try {
      const gaName = toGaEventName(eventName);
      window.gtag('event', gaName, {
        send_to: GA4_MEASUREMENT_ID,
        event_id,
        transaction_id: event_id,
        event_source: 'website',
        button_label: customData.button_label || '',
        section: customData.section || '',
        source: customData.source || '',
        sede: customData.sede || '',
      });
    } catch (err) { console.debug('[Sfarinà Tracking] gtag fire failed', err); }
  }
};

/**
 * Spara la conversione Google Ads "Lead generato" con Enhanced Conversions
 * (email + phone passati a gtag, Google fa hashing automatico).
 */
const fireGoogleAdsLeadConversion = (event_id, leadData = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    // Enhanced Conversions: gtag('set', 'user_data', {...}) prima della conversion.
    // Google hasha automaticamente lato browser prima di inviare a Google Ads.
    if (leadData.email || leadData.phone || leadData.first_name) {
      window.gtag('set', 'user_data', {
        email: (leadData.email || '').trim().toLowerCase(),
        phone_number: (leadData.phone || '').trim(),
        address: {
          first_name: (leadData.first_name || '').trim().toLowerCase(),
          last_name: (leadData.last_name || '').trim().toLowerCase(),
        },
      });
    }
    window.gtag('event', 'conversion', {
      send_to: GOOGLE_ADS_LEAD_CONVERSION_ID,
      value: leadData.value != null ? leadData.value : 0,
      currency: leadData.currency || 'EUR',
      transaction_id: event_id,
    });
  } catch (err) { console.debug('[Sfarinà Tracking] Google Ads conversion fire failed', err); }
};

/* ================================================================ */
/* SERVER-SIDE FORWARDER (backend → Meta CAPI + GA4 MP)             */
/* ================================================================ */
const sendToBackend = (eventName, eventData, event_id, isLead) => {
  if (!BACKEND_URL) return;

  // PII vanno al backend SOLO sul Lead (l'utente ha appena dato consenso esplicito tramite form).
  // Per gli altri eventi, eventuali PII persistite in sessione vengono inviate solo se hasMarketingConsent.
  const persistedLead = getPersistedLeadDataIfAllowed();
  const lead = isLead ? eventData : persistedLead;

  const payload = {
    event_name: eventName,
    event_id,
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: typeof window !== 'undefined' ? window.location.href : '',
    user_data: {
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      fbclid: getCookie('fbclid'),
      ga_client_id: getGaClientId(),
      ga_session_id: getGaSessionId(),
      client_ip_address: cachedIp,
      client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    },
    custom_data: {
      ...(eventData || {}),
      // Non passare le PII al custom_data del CAPI (sono già in user_data)
      first_name: undefined,
      last_name: undefined,
      email: undefined,
      phone: undefined,
      privacy_consent: undefined,
    },
  };

  // fire-and-forget, keepalive così sopravvive al navigation
  fetch(TRACKING_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch((err) => {
    if (typeof window !== 'undefined' && window.console) {
      console.warn('[Sfarinà Tracking] backend error', err);
    }
  });
};

/* ================================================================ */
/* MAKE.COM WEBHOOK — chiamato SOLO sul Lead per notifiche interne  */
/* ================================================================ */
const sendLeadToWebhook = (eventData, event_id) => {
  if (!WEBHOOK_URL) return;
  const payload = {
    event_name: 'Lead',
    event_id,
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: typeof window !== 'undefined' ? window.location.href : '',
    page_title: typeof document !== 'undefined' ? document.title : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    consent: { iubenda_marketing: hasMarketingConsent() },
    user_data: {
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      fbclid: getCookie('fbclid'),
      gclid: getGclidFromCookies(),
      gbraid: getCookie('gbraid'),
      wbraid: getCookie('wbraid'),
      ga_client_id: getGaClientId(),
      client_ip_address: cachedIp,
      client_user_agent: getCookie('client_user_agent') || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    },
    custom_data: {
      first_name: eventData.first_name || '',
      last_name: eventData.last_name || '',
      email: eventData.email || '',
      phone: eventData.phone || '',
      privacy_consent: eventData.privacy_consent === true,
      source: eventData.source || '',
      offer: eventData.offer || '',
    },
  };
  fetch(WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch((err) => {
    if (typeof window !== 'undefined' && window.console) {
      console.warn('[Sfarinà Tracking] webhook error', err);
    }
  });
};

/* ================================================================ */
/* PUBLIC API                                                       */
/* ================================================================ */
/**
 * Invia un evento al tracking pipeline:
 *  1. Client-side: Meta Pixel + GA4 (sempre)
 *  2. Server-side: backend /api/tracking/event → Meta CAPI + GA4 MP (sempre)
 *  3. Make.com webhook: SOLO se isLead=true (notifiche interne)
 *  4. Google Ads conversion + Enhanced Conversions: SOLO se isLead=true
 *
 * @returns {Promise<{event_id:string}>}
 */
export const sendServerSideEvent = async (eventName, eventData = {}, providedEventId) => {
  const event_id = providedEventId || generateEventId();
  const isLead = eventName === 'Lead';

  // 1. Client-side pixels (immediato)
  fireClientSidePixels(eventName, event_id, eventData);

  // 2. Su Lead, spara anche Google Ads conversion con Enhanced Conversions
  if (isLead) {
    fireGoogleAdsLeadConversion(event_id, eventData);
  }

  // 3. Server-side relay (Meta CAPI + GA4 MP). Aspetta IP fino a 1.2s.
  if (!cachedIp) {
    await Promise.race([fetchPublicIp(), new Promise((r) => setTimeout(r, 1200))]);
  }
  sendToBackend(eventName, eventData, event_id, isLead);

  // 4. Make.com webhook (solo Lead, per notifiche interne)
  if (isLead) {
    sendLeadToWebhook(eventData, event_id);
  }

  // dataLayer push (utile per debug / GTM esterno se mai riattivato)
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'sfarina_' + eventName.toLowerCase(),
      sfarina_event_id: event_id,
    });
  }

  return { event_id, response: null };
};

/** Helper retro-compatibile: ritorna direttamente event_id. */
export const trackEventDedup = async (eventName, eventData = {}) => {
  const { event_id } = await sendServerSideEvent(eventName, eventData);
  return event_id;
};
